import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useMemo, useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import type { TableBlockProps, TableBody, TableColumn, TableHeader } from './TableBlock.data';
import styles from './TableBlock.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './TableBlock.transitions';

export default function TableBlock({
  isVisible,
  className,
  blok,
  ref,
}: TableBlockProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const hasBody = useMemo(() => {
    let isEmpty = true;
    blok.table.tbody.some((tableRow) => {
      let hasValue = false;
      tableRow.body.some((cell) => {
        if (cell.value) {
          isEmpty = false;
          hasValue = true;
          return true;
        }
        return false;
      });
      return hasValue;
    });
    return !isEmpty;
  }, [blok.table.tbody]);

  const hasHeader = useMemo(() => {
    let isEmpty = true;
    blok.table.thead.some((cell) => {
      if (cell.value) {
        isEmpty = false;
        return true;
      }
      return false;
    });
    return !isEmpty;
  }, [blok.table.thead]);

  if (!hasHeader && !hasBody) {
    return <div ref={elementRef} {...sbEditable(blok)} />;
  }

  return (
    <div
      ref={elementRef}
      className={classNames(styles.tableBlock, className)}
      {...sbEditable(blok)}
    >
      <table>
        {hasHeader && (
          <thead>
            <tr>
              {blok.table.thead &&
                blok.table.thead.map((theadItem: TableHeader) => (
                  <th key={theadItem.value}>
                    <div className={styles.tableTitle}>{theadItem.value}</div>
                  </th>
                ))}
            </tr>
          </thead>
        )}

        {hasBody && (
          <tbody>
            {blok.table.tbody &&
              blok.table.tbody.map((tbodyItem: TableBody) => (
                <tr key={tbodyItem._uid}>
                  {tbodyItem.body.map((tbodyItemValue: TableColumn) => (
                    <td key={`cell${tbodyItemValue.value}`}>
                      <div className={styles.paragraph}>{tbodyItemValue.value}</div>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        )}
      </table>
    </div>
  );
}
